import { IComplianceInfo } from "api/compliance/types";
import {
	CorporateInvestorClassificationsMap,
	getInvestorClassificationOptions,
	InvestorClassificationOption,
} from "pages/InvestorPage/Corporate/CompanyInfo/components/Declaration/utils/investorClassification";
import {
	INACTIVE_CLASSIFICATION_ERROR,
	IProfileCompleteForVerification,
	isProfileCompleteWithAmlApproved,
} from "utils/individualInvestorClassification";

export { INACTIVE_CLASSIFICATION_ERROR };

export function isCorporateInvestorUser(permissions: string[] = []): boolean {
	return permissions.includes("WEB_INVESTOR_CORP_MANAGE");
}

export interface ICorporateInvestorProgressForVerification {
	corporateStatus?: string;
}

export function isFullyVerifiedCorporateInvestor(
	profileComplete?: IProfileCompleteForVerification | null,
	corporateProgress?: ICorporateInvestorProgressForVerification | null,
): boolean {
	return (
		isProfileCompleteWithAmlApproved(profileComplete) &&
		corporateProgress?.corporateStatus === "APPROVED"
	);
}

export function needsCorporateClassificationUpdate(compliance?: IComplianceInfo | null): boolean {
	if (!compliance) return false;

	const ids = compliance.investorClassificationCriteriaIds ?? [];
	if (!ids.length) return true;

	const statuses = compliance.investorClassificationCriteriaStatuses ?? [];
	if (!statuses.length) return false;

	return statuses.some((status) => status !== "ACTIVE");
}

export function getInitialCorporateClassificationIds(
	compliance?: IComplianceInfo | null,
): number[] {
	const ids = compliance?.investorClassificationCriteriaIds ?? [];
	const statuses = compliance?.investorClassificationCriteriaStatuses ?? [];

	if (!ids.length) return [];
	if (statuses.length !== ids.length) return [];

	return ids.filter((_, index) => statuses[index] === "ACTIVE");
}

export function hasSelectedInactiveProfileCorporateClassifications(
	selectedIds: number[] = [],
	compliance?: IComplianceInfo | null,
): boolean {
	if (!selectedIds.length || !compliance) return false;

	const profileIds = compliance.investorClassificationCriteriaIds ?? [];
	const statuses = compliance.investorClassificationCriteriaStatuses ?? [];

	return selectedIds.some((selectedId) => {
		const index = profileIds.findIndex((id) => Number(id) === Number(selectedId));
		if (index === -1) return false;

		return statuses[index] !== "ACTIVE";
	});
}

/** Inactive saved classifications for the dropdown when metadata no longer includes them. */
export function getInactiveProfileCorporateClassificationOptions(
	compliance?: IComplianceInfo | null,
	investorType?: string | null,
	classifications: CorporateInvestorClassificationsMap = {},
): InvestorClassificationOption[] {
	if (!compliance || !investorType) return [];
	if (investorType !== compliance.investorType) return [];

	const ids = compliance.investorClassificationCriteriaIds ?? [];
	const labels = compliance.investorClassificationCriteriaLabels ?? [];
	const statuses = compliance.investorClassificationCriteriaStatuses ?? [];
	const allItems = Object.values(classifications).flat();
	const activeIdSet = new Set(
		getInvestorClassificationOptions(investorType, classifications).map((option) =>
			Number(option.value),
		),
	);

	const options: InvestorClassificationOption[] = [];

	ids.forEach((id, index) => {
		if (statuses[index] === "ACTIVE") return;
		if (activeIdSet.has(Number(id))) return;
		if (options.some((option) => Number(option.value) === Number(id))) return;

		const metadataItem = allItems.find((item) => Number(item.id) === Number(id));
		const label = metadataItem?.label ?? labels[index];
		if (!label) return;

		options.push({ label, value: String(id), isDisabled: true });
	});

	return options;
}

/** Active + saved inactive (disabled) options for the corporate requirement modal. */
export function getCorporateRequirementClassificationOptions(
	investorType: string | undefined,
	classifications: CorporateInvestorClassificationsMap = {},
	compliance?: IComplianceInfo | null,
): InvestorClassificationOption[] {
	if (!investorType) return [];

	const options = getInvestorClassificationOptions(investorType, classifications).map((option) => ({
		...option,
		isDisabled: false,
	}));

	const savedIds = compliance?.investorClassificationCriteriaIds ?? [];
	const savedLabels = compliance?.investorClassificationCriteriaLabels ?? [];
	const savedStatuses = compliance?.investorClassificationCriteriaStatuses ?? [];
	const savedCriteria = compliance?.investorClassificationCriteria ?? [];

	const addInactiveOption = (id: number, label: string) => {
		const value = String(id);
		const existingIndex = options.findIndex((option) => option.value === value);

		if (existingIndex >= 0) {
			options[existingIndex] = { ...options[existingIndex], isDisabled: true };
			return;
		}

		options.push({ label, value, isDisabled: true });
	};

	savedIds.forEach((id, index) => {
		if (savedStatuses[index] === "ACTIVE") return;

		const fromCriteria = savedCriteria.find((item) => Number(item.id) === Number(id));
		const label = fromCriteria?.label ?? savedLabels[index];
		if (!label) return;

		addInactiveOption(Number(id), label);
	});

	getInactiveProfileCorporateClassificationOptions(compliance, investorType, classifications).forEach(
		(option) => addInactiveOption(Number(option.value), option.label),
	);

	return options;
}
